import { useState } from 'react';
import { Link } from 'react-router-dom';
import useStoreSubdomain, { useStorePath } from '@/hooks/useStoreSubdomain';
import useStoreBranding from '@/hooks/useStoreBranding';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import StoreBrandMark from '@/components/StoreBrandMark';
import PasswordInput from '@/components/PasswordInput';
import { Loader2, ArrowLeft } from 'lucide-react';
import { DEFAULT_STORE_PRIMARY, DEFAULT_STORE_PRIMARY_HOVER } from '@/lib/storeTheme';

export default function StoreRegister() {
  const subdomain = useStoreSubdomain();
  const storePath = useStorePath();
  const { theme, loading: brandingLoading } = useStoreBranding();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const primary = theme?.primary || DEFAULT_STORE_PRIMARY;
  const primaryHover = theme?.primaryHover || DEFAULT_STORE_PRIMARY_HOVER;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Password dan konfirmasi password tidak cocok');
      return;
    }
    if (password.length < 6) {
      setError('Password minimal 6 karakter');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/store/register-customer', {
        name,
        phone,
        email,
        password,
        tenant_subdomain: subdomain
      });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      window.location.href = '/my';
    } catch (err) {
      setError(err.response?.data?.detail || 'Registrasi gagal. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center pb-2">
          <Link to={storePath('/')} className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4 justify-center">
            <ArrowLeft size={16} className="mr-1" /> Kembali ke Toko
          </Link>
          <StoreBrandMark theme={theme} loading={brandingLoading} />
          <CardTitle className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>Daftar Akun Pelanggan</CardTitle>
          <CardDescription>Buat akun untuk belanja, pantau pesanan, dan akses kursus</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="name">Nama Lengkap</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nama lengkap Anda"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="phone">Nomor WhatsApp</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="08xxxxxxxxxx"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@contoh.com"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimal 6 karakter"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Konfirmasi Password</Label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ulangi password"
                required
                className="mt-1"
              />
            </div>
            <Button
              type="submit"
              className="w-full text-white"
              style={{ backgroundColor: primary }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = primaryHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = primary; }}
              disabled={loading}
            >
              {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
              Daftar
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-500">
            Sudah punya akun?{' '}
            <Link to={storePath('/login')} className="font-medium hover:underline" style={{ color: primary }}>
              Masuk di sini
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
